import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import WarningIcon from '@mui/icons-material/Warning'
import { makeStyles } from '@mui/styles'
import { Grid, IconButton, Theme, Typography } from '@mui/material'
import CardComponent from '@professocial/professocialFormComponents/FormComponents/CardComponent'
import EyeIcon from '@mui/icons-material/RemoveRedEye'

import EngagementRate from '../../../shared/icons/EngagementRate'
import { IGoogleMyBusinessPostProps } from './GoogleMyBusinessPostTemplate'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import getDateFormat from '../../../defaults/getDateFormat'
import { getTheme, getUser } from '../../../store/common/common.selectors'
import { useSelector } from 'react-redux'
import {
  ActionType,
  DeferredPostStatus,
  NetworkType,
} from '@3m5/sm_publish-frontend-api'
import { MenuItems } from '@professocial/professocialFormComponents/FormComponents'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { NetworkPage, PostingTemplate, PreviewFormat } from '@professocial/professocialTypes/types/commonTypes'
import { getFacebookPage } from '../../../store/facebook/facebook.selector'
import { getInstagramPage } from '../../../store/instagram/instagram.selector'
import { getGooglePage } from '../../../store/googleMyBusiness/googleMyBusiness.selector'
import ProfessocialPosting from '../../ProfessocialPosting'
import checkUrl from '../../../util/checkUrl'

const GoogleMyBusinessOverview: React.FC<IGoogleMyBusinessPostProps> = (props: IGoogleMyBusinessPostProps) => {
  const theme: Theme = useSelector(getTheme)
  const user = useSelector(getUser)
  const facebookPage = useSelector(getFacebookPage)
  const instagramPage = useSelector(getInstagramPage)
  const googleMyBusinessPage = useSelector(getGooglePage)

  const postingSuccessListener = (({ detail }: CustomEvent) => {
    if (detail) {
      setTimeout(() => {
        setOpenEditPost(false)
      }, 500)
    }
  }) as EventListener
  
  
  useEffect(() => {
    window.addEventListener('ProfessocialPostingSuccess', postingSuccessListener)

    return (() => {
      window.removeEventListener('ProfessocialPostingSuccess', postingSuccessListener)
    })
  },
  [],
  )

  const networkPage: NetworkPage = {}
  switch (props.networkType) {
  case NetworkType.Facebook:
    networkPage.facebookPage = facebookPage
    break
  case NetworkType.Instagram:
    networkPage.instagramPage = instagramPage
    break
  case NetworkType.GoogleMyBusiness:
    networkPage.googleMyBusinessPage = googleMyBusinessPage
    break
  }

  const [post, setPost] = useState<any>(props.post)
  const [postStatus, setPostStatus] = useState<DeferredPostStatus | undefined>(post?.status)
  const [menuItems, setMenuItems] = useState<MenuItems[]>([])
  const [postExtensionLink, setPostExtensionLink] = useState<string>('')
  const [openEditPost, setOpenEditPost] = useState<boolean>(false)

  const { t } = useTranslation()

  const useStyles = makeStyles(() => ({
    iconContainer: {
      marginTop: '5px',
      display: 'flex',
      flexWrap: 'nowrap',
    },
    text: {
      color: theme.palette.text.primary,
      maxHeight: '70px',
      overflow: 'hidden',
    },
    card: {
      height: '225px',
      width: props.width && (props.width >= 600 ? props.width : props.width / 2),
    },
    image: {
      display: 'grid',
      justifyItems: 'center',
      height: 'auto',
      width: '100%',
      overflow: 'hidden',
    },
    container: {
      padding: '5px 20px',
    },
    typeIcon: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      color: theme.palette.primary.main,
      margin: theme.spacing(),
    },
    error: {
      color: theme.palette.error.main,
      display: 'flex',
    },
    postExtensionButton: {
      minWidth: '180px',
      '&:hover': {
        color: '#FFFFFF',
      },
    },
  }))

  const classes = useStyles()

  useEffect(() => {
    setPostStatus(post?.status)
    const postStarted: boolean = new Date(post.postStart).getSeconds() <= new Date().getSeconds()
    const items = [
      {
        label: t('postCommon:edit'),
        value: 'edit',
        handleClick: () => { setOpenEditPost(true) },
      },
    ]
    if (post.status === DeferredPostStatus.AwaitingUserConfirmation) {
      // manuelle Posts dürfen gelöscht oder bestätigt werden
      items.push({
        label: t('postCommon:retry'),
        value: 'confirm',
        handleClick: () => { props.onConfirmPost && props.onConfirmPost(post.id) },
      },
      {
        label: t('postCommon:delete'),
        value: 'decline',
        handleClick: () => { props.onDeclinePost && props.onDeclinePost(post.id) },
      })
    } else {
      // PostingError
      if (post.status === DeferredPostStatus.TokenInvalid ||
        post.status === DeferredPostStatus.ErrorWhilePosting ||
        post.status === DeferredPostStatus.RetryScheduled
      ) {
        if (postStarted) {
          items.push({
            label: t('postCommon:retry'),
            value: 'retry',
            handleClick: () => { props.onRetryPost && props.onRetryPost(post.id) },
          })
        }
        items.push({
          label: t('postCommon:delete'),
          value: 'delete',
          handleClick: () => { props.onDeletePost && props.onDeletePost(post.id) },
        })
      }
      // scheduled posts
      if (post.status === DeferredPostStatus.Queued ||
        post.status === DeferredPostStatus.Processing ||
        post.status === DeferredPostStatus.Scheduled
      ) {
        items.push({
          label: t('postCommon:delete'),
          value: 'delete',
          handleClick: () => {
            props.onDeletePost && props.onDeletePost(post.id)
          },
        })
      }
      // publishedPosts GMB
      if (!post.status) {
        items.push({
          label: t('postCommon:delete'),
          value: 'delete',
          handleClick: () => {
            props.onDeleteFeed && props.onDeleteFeed(post.id)
          },
        })
      }
    }
    setMenuItems(items)

    // ggf. http:// davor setzen
    const url = checkUrl(post?.ctaButton?.url || post?.overridingPostContent?.extensions?.googleMyBusinessExtension?.ctaLink)
    url && setPostExtensionLink(url)
  }, [post])

  useEffect(() => {
    setPost(props.post)
  }, [props.post])

  const mapToPostingTemplate = (post: any): PostingTemplate | null => {
    // GooglePost | PostingTemplate | DeferredPost
    const postingTemplate: PostingTemplate | null = {
      content: post.overridingPostContent || post.content || {
        text: post.message || post.overridingPostContent?.text,
        extensions: {},
      },
      pagePostId: post.message && post.id,
      socialMediaNetwork: {
        googleMyBusiness: true,
      },
      postStart: post.createdAt || post.postStart,
      postEnd: post.postEnd,
      aboTopicId: post.aboTopicId,
      postTemplateId: post.postTemplateId || post.templateId,
      deferredPostId: post.source && post.id,
    }
    if (postingTemplate && post.mediaUrls && post.mediaUrls[0]) {
      postingTemplate.content.image = {
        url: post.mediaUrls[0],
      }
    }
    if (postingTemplate && post.ctaButton) {
      postingTemplate.content.extensions = {
        googleMyBusinessExtension: {
          ctaActionType: post.ctaButton && post.ctaButton.actionType,
          ctaLink: post.ctaButton && post.ctaButton.url,
        },
      }
    }
    return postingTemplate
  }
  const ctaButtonType = post?.ctaButton?.actionType || post?.overridingPostContent?.extensions?.googleMyBusinessExtension?.ctaActionType

  return (
    <>
      <Grid item xs={12} className={classes.card}>
        <CardComponent
          class={classes.card}
          images={post?.mediaUrls || (post?.overridingPostContent?.image?.url && [post?.overridingPostContent?.image?.url])}
          imgClassName={classes.image}
          imgWidth='171px'
          maxHeight={150}
          content={
            <Grid container className={classes.container}>
              {(post?.createdAt || post?.postStart) &&
                <Grid item xs={12}>
                  <>
                    {post?.createdAt &&
                      <Typography
                        style={{ marginBottom: '10px' }}
                        className={classes.text}
                      >
                        <strong>{format((post?.createdAt), getDateFormat())}</strong>
                      </Typography>}
                    {post?.postStart &&
                      <Typography
                        style={{ marginBottom: '10px' }}
                        className={`${classes.text} ${postStatus && (postStatus === DeferredPostStatus.TokenInvalid || postStatus === DeferredPostStatus.ErrorWhilePosting) && classes.error}`}
                      >
                        <strong>{`${format((post?.postStart), getDateFormat())} - ${t(`postCommon:deferredPostStatus${postStatus}`)}`}</strong>
                        {postStatus && (postStatus === DeferredPostStatus.TokenInvalid || postStatus === DeferredPostStatus.ErrorWhilePosting) &&
                          <WarningIcon style={{ margin: '0 5px' }} />}
                      </Typography>}
                  </>
                </Grid>}
              <Grid item xs={12}>
                <Typography
                  className={classes.text}
                  paragraph sx={{ wordWrap: 'break-word' }}
                >{post?.message || post?.overridingPostContent?.text}
                </Typography>
              </Grid>
              {ctaButtonType && ctaButtonType !== ActionType.None &&
                <Grid item xs={12} sx={{ marginBottom: '10px', whiteSpace: 'nowrap' }}>
                  <ButtonComponent
                    className={classes.postExtensionButton}
                    color='primary'
                    text={t(`postCommon:actionButton${post?.ctaButton?.actionType || post?.overridingPostContent?.extensions?.googleMyBusinessExtension?.ctaActionType}`)}
                    type='button'
                    link={postExtensionLink}
                  />
                </Grid>}
            </Grid>
          }
          key={post.id ? +post.id : 1}
          menuItem={menuItems}
          actions={
            <>
              {post.googlePostStatistic &&
                <Grid container className={classes.container}>
                  <div className={classes.iconContainer}>
                    <IconButton disabled>
                      <EyeIcon className={classes.typeIcon} />
                      <Typography className={classes.text}>{post.googlePostStatistic?.views || 0}</Typography>
                    </IconButton>
                    <IconButton disabled>
                      <EngagementRate className={classes.typeIcon} />
                      <Typography className={classes.text}>{post.googlePostStatistic?.clicks || 0}</Typography>
                    </IconButton>
                  </div>
                </Grid>}
            </>
          }
        />
      </Grid>
      {/* Dialog zum Bearbeiten */}
      <DialogComponent
        open={openEditPost}
        autoClose={false}
        closeIcon
        onCancel={() => setOpenEditPost(false)}
        onClose={() => setOpenEditPost(false)}
        title={t('postCommon:editPost')}
        content={
          <ProfessocialPosting
            postingConfiguration={{
              generalPostingOptions: {
                allowLinkParsing: false,
                allowTemplateEdit: false,
                showAsPopUp: true,
                showPreview: false,
                templateSelection: false,
                useMediaGallery: false,
                allowLinkIndividualisation: false,
                allowIndividualHashtags: false,
                allowTags: false,
                allowImageUpload: true,
                allowVideoUpload: false,
                maxCharacters: 1500,
                previewFormat: PreviewFormat.origin,
              },
              networkPage,
              user,
              networkUserId: props.networkUserId,
              theme,
              licensePermissions: { googleMyBusiness: true },
              post: mapToPostingTemplate(props.post),
            }}
          />
        }
      />
    </>
  )
}

export default GoogleMyBusinessOverview
